<template>
  <div class="ErrorPage">
    <AppHeader hideActions />

    <main class="ErrorPage-content PageContainer u-container">
      <div class="ErrorPage-information">
        <div class="ErrorPage-text">
          <div class="ErrorPage-titleGroup">
            <h1 class="ErrorPage-errorCode">{{ error?.statusCode || '000' }}</h1>
            <h2 class="ErrorPage-title">{{ errorTitle }}</h2>
          </div>

          <p class="ErrorPage-description">{{ errorDescription }}</p>
        </div>

        <ActionButton
          class="ErrorPage-button"
          variant="primary"
          size="lg"
          rightIcon="chevron-right"
          @click="handleError"
        >
          {{ t('ui.error.goHome') }}
        </ActionButton>
      </div>

      <img
        :src="errorImage"
        class="ErrorPage-illustration"
        :width="imageSize"
        :height="imageSize"
        alt=""
      >
    </main>
  </div>
</template>

<script setup lang="ts">
import type { NuxtError } from '#app';
import BrokenFace from '~/assets/img/errors/broken-face-box.svg';
import Toolbox from '~/assets/img/errors/tools-box.svg';

interface Props {
  error: NuxtError;
}

const props = defineProps<Props>();
const { t } = useI18n();
const { isMobile } = useResponsive();

const errorCode = computed(() => {
  return props.error?.statusCode?.toString() || '000';
});

const errorTitle = computed(() => {
  return t(`ui.error.code.${errorCode.value}.title`);
});

const errorDescription = computed(() => {
  return t(`ui.error.code.${errorCode.value}.description`);
});

const errorImage = computed(() => {
  // TODO CVO-19900 Update this when implementing actual maintainance mode.
  const isMaintainance = props.error.message.includes('Maintainance');
  return isMaintainance ? Toolbox : BrokenFace;
});

const imageSize = computed(() => {
  return isMobile.value ? '120' : '235';
});

function handleError() {
  clearError({ redirect: '/' });
};
</script>

<style lang="scss">
@use 'assets/scss/theme' as vars;
@use '@cvo/ui/styles/utils/text';
@use '@cvo/ui/styles/utils/responsive';

@layer cvo-base {
  .ErrorPage {
    background-color: vars.$templates-systemerror-bg-color;

    &-content {
      flex: 1;
      display: flex;
      gap: vars.$dimensions-d-14; // 56px gap between elements
      align-items: flex-start;
      justify-content: start;
      padding: vars.$dimensions-d-24 vars.$dimensions-d-30;

      @include responsive.mobile {
        flex-direction: column-reverse;
        gap: vars.$dimensions-d-14; // 56px gap between elements
        align-items: center;
        justify-content: center;
        padding: vars.$dimensions-d-5;
      }
    }

    &-information {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      align-items: start;
      gap: vars.$dimensions-d-16;
      @include responsive.mobile {
        align-items: stretch;
      }
    }

    &-text {
      display: flex;
      flex-direction: column;
      gap: vars.$dimensions-d-6;
    }

    &-errorCode {
      color: vars.$templates-systemerror-title-color;
      @include text.heading('h1');
    }

    &-title {
      color: vars.$templates-systemerror-title-color;
      @include text.heading('h2');
    }

    &-description {
      color: vars.$templates-systemerror-subtitle-color;
      @include text.text('xl');
    }
  }
}
</style>
